import React from "react";
import { motion } from "framer-motion";
import { Coffee, Flame, Timer, CheckCircle, CoffeeIcon, Sparkles, CircleSlash, Sun } from "lucide-react";

export default function CoffeeCaffeinePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-amber-100 to-amber-50 pt-24 pb-16 px-4 flex justify-center">
      <motion.article
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl w-full bg-white/80 shadow-2xl border border-amber-100 rounded-2xl p-6 backdrop-blur-md"
      >
        {/* HEADER */}
        <div className="w-full flex flex-col items-center gap-4 mb-8">

          <h1 className="text-xl font-bold text-amber-900 text-center">
            یک بررسی علمی – تاریخی – کاربردی برای مصرف‌کنندگان و فعالان صنعت قهوه
          </h1>
          <img
            src="/images/coffeeLoading.jpg"
            className="rounded-xl shadow-md w-full max-h-72 object-cover"
            alt="Caffeine and Coffee"
          />

          <div className="text-sm text-stone-700 leading-7 text-justify px-2">
            <p className="font-bold text-amber-900 mb-2">
              مقدمه: ماده‌ای کوچک با شهرتی جهانی
            </p>
            <p>
              هر روز میلیون‌ها نفر در سراسر دنیا صبح خود را با یک فنجان قهوه آغاز
              می‌کنند؛ فنجانی که نه‌تنها بخشی از فرهنگ جهانی شده، بلکه به‌دلیل وجود
              ترکیبی به نام کافئین، نقشی مهم در تمرکز، انرژی و هشیاری انسان دارد.
            </p>
            <p className="mt-2">
              با این حال، دربارهٔ کافئین باورهای درست و غلط زیادی وجود دارد:
              «رست تیره کافئین بیشتری دارد»، «قهوه اعتیادآور است»،
              «کافئین باعث رشد قد نمی‌شود»، «عربیکا کم‌کافئین است اما خوش‌عطر»،
              «روبوستا انرژی‌بخش‌تر است» و…
            </p>
            <p className="mt-2">
              در این مقاله تلاش کرده‌ایم بر پایهٔ منابع معتبر علمی، تصویری روشن و
              واقع‌بینانه از کافئین ارائه دهیم؛ تصویری که هم برای مصرف‌کنندگان قابل
              فهم باشد و هم برای تولیدکنندگان و رُسترهای حرفه‌ای کاربردی.
            </p>
          </div>

        </div>
        <Divider />

        {/* SECTION 1 */}
        <Section
          title="۱. کافئین چیست و چگونه عمل می‌کند؟"
          icon={<CoffeeIcon className="w-5 h-5 text-amber-900" />}
          image="/images/caffein.jpg"
        >
          <p>
            کافئین یک آلکالوئید طبیعی است که در گیاه قهوه نقش دفاعی دارد و
            با افزایش تلخی٬ از گیاه در برابر حشرات محافظت می‌کند.
          </p>
          <div>در بدن انسان، کافئین وظیفه‌اش این است که:</div>
          <ul className="mt-3 list-disc pr-6 space-y-1 text-sm">
            <li>گیرنده‌های «آدنوزین» را مسدود کند (آدنوزین ماده‌ای است که احساس خستگی ایجاد می‌کند)</li>
            <li>فعالیت دوپامین و نورآدرنالین را افزایش دهد</li>
            <li>توان ذهنی، سرعت واکنش و تمرکز را بالا ببرد</li>
          </ul>
          <p className="mt-2 text-sm">
            بنابراین کافئین «انرژی تولید نمی‌کند»، بلکه احساس خستگی را کاهش می‌دهد.
            نیمه‌عمر کافئین (زمانی که نصف آن در بدن باقی می‌ماند) بین ۳ تا ۵ ساعت است، اما در برخی افراد می‌تواند تا ۱۰ ساعت هم طول بکشد.
          </p>
        </Section>

        <Divider />

        {/* SECTION 2 */}
        <Section
          title="۲. تفاوت مقدار کافئین در عربیکا و روبوستا"
          icon={<Coffee className="w-5 h-5 text-amber-900" />}
          image="/images/arabicavsrobusta.jpg"
        >
          <div>یکی از واقعیت‌های مهمی که باید دانست این است که <strong>روبوستا ذاتاً پرکافئین‌تر از عربیکاست</strong>.</div>
          <p>مقدار کافئین در این دو گونه:</p>
          <ul className="mt-3 list-disc pr-6 space-y-1 text-sm">
            <li>عربیکا: ۰.۸٪ تا ۱.۴٪</li>
            <li>روبوستا: ۱.۷٪ تا ۴٪</li>
          </ul>
          <p className="mt-2 text-sm">
            بنابراین روبوستا تقریباً دو برابر عربیکا کافئین دارد.
            این تفاوت به دلیل ژنتیک گیاه و شرایط رشد آن است. روبوستا برای بقا در مقابل بیماری‌ها و آفات، کافئین بیشتری تولید می‌کند.
          </p>
          <div>
            <b>نتیجه کاربردی:</b>
            <p>اگر مشتری قهوه‌ای با انرژی و تلخی بیشتر می‌خواهد → ترکیب‌های شامل روبوستا بهترین انتخاب‌اند.</p>
          </div>
        </Section>

        <Divider />

        {/* SECTION 3 */}
        <Section
          title="۳. آیا رست تیره کافئین بیشتری دارد؟"
          icon={<Flame className="w-5 h-5 text-amber-900" />}
          image="/images/darkroast.jpg"
        >
          <p className="text-sm font-semibold text-amber-800">❌ باور غلط:</p>
          <p className="text-sm mb-2">رست تیره پرکافئین‌تر است.</p>

          <p className="text-sm font-semibold text-green-700 mt-1">✔️  حقیقت علمی:</p>
          <p className="text-sm">
            شدت رُست تأثیر قابل‌توجهی بر مقدار کافئین ندارد.
          </p>
          <div>
            <strong>توضیح علمی:</strong>
            <ul className="mt-3 list-disc pr-6 space-y-1 text-sm">
              <li>کافئین در دماهای بالا پایدار است و در فرایند رُست از بین نمی‌رود.</li>
              <li>آنچه تغییر می‌کند چگالی دانه است.</li>
              <li>دانه رست روشن سنگین‌تر و متراکم‌تر است؛ رست تیره حجیم‌تر و سبک‌تر.</li>
            </ul>
          </div>
          <div>اگر بر اساس وزن٬ قهوه را اندازه بگیریم، یعنی ۱۰ گرم لایت رست در برابر ۱۰ گرم دارک رست،</div>
          <strong>عملاً مقدار کافئین هر دو بسیار نزدیک به هم است.</strong>
        </Section>

        <Divider />

        {/* SECTION 4 */}
        <Section
          title="۴. روش دم‌آوری چه تأثیری بر کافئین دارد؟"
          icon={<Timer className="w-5 h-5 text-amber-900" />}
          image="/images/brewcoffee.jpg"
        >
          <div>میزان کافئین نهایی در فنجان بیشتر از هر چیز به روش استخراج بستگی دارد.<br />
            از نظر خروجی کافئین:</div>
          <p className="text-sm mb-2">دم‌آوری طولانی ⟵ کافئین بیشتر</p>
          <ul className="list-disc pr-6 text-sm space-y-1">
            <li>فرنچ‌پرس</li>
            <li>V60</li>
            <li>کمکس</li>
            <li>قهوه دمی غلیظ</li>
          </ul>

          <p className="text-sm mt-3 mb-1">دم‌آوری کوتاه ⟵ کافئین کمتر</p>
          <ul className="list-disc pr-6 text-sm space-y-1">
            <li>اسپرسو (زمان بسیار کوتاه است)</li>
            <div className="flex gap-3">
              <Sparkles className="text-yellow-600 w-7 h-7" />
              <p>البته اسپرسو در هر میلی‌لیتر پرکافئین است، اما چون حجم آن کم است، کافئین کل فنجان کمتر از قهوه دمی است.</p>
            </div>
          </ul>

        </Section>

        <Divider />

        {/* SECTION 5 */}
        <Section
          title="۵.کافئین چه اثری بر بدن دارد؟ (فواید و هشدارها)"
          icon={<CheckCircle className="w-5 h-5 text-green-700" />}
          image="/images/prosandcons.jpg"
        >
          <p className="font-medium text-amber-800 text-sm">فواید (طبق تحقیقات FDA و Harvard School of Public Health)::</p>
          <ul className="list-disc pr-6 text-sm space-y-1">
            <li>فزایش تمرکز و کارایی ذهنی</li>
            <li>کاهش احساس خستگی</li>
            <li>بهبود عملکرد ورزشی</li>
            <li>کمک به متابولیسم چربی</li>
            <li>احتمال کاهش خطر آلزایمر و پارکینسون در مصرف متعادل</li>
          </ul>

          <strong>مقدار مصرف ایمن:</strong>
          <p>حداکثر ۴۰۰ میلی‌گرم در روز برای بزرگسالان سالم
            (معادل حدود ۳ تا ۴ فنجان قهوه معمولی)</p>

          <p className="font-medium text-red-700 mt-3 text-sm">
            هشدارها (افرادی که باید کمتر مصرف کنند):
          </p>
          <ul className="list-disc pr-6 text-sm space-y-1">
            <li>افراد حساس به کافئین</li>
            <li>افرادی با اضطراب بالا</li>
            <li>کسانی که مشکل خواب دارند</li>
            <li>زنان باردار (حداکثر ۲۰۰ میلی‌گرم در روز)</li>
          </ul>
        </Section>

        <Divider />

        <Section
          title="۶.باورهای غلط درباره کافئین"
          icon={<CircleSlash className="w-5 h-5 text-red-700" />}
          image="/images/wrong.jpg"
        >
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full bg-white border border-amber-200 rounded-lg overflow-hidden text-sm">
              <thead className="bg-amber-100 text-amber-900">
                <tr>
                  <th className="py-3 px-4 border-b border-amber-200 text-right font-semibold">
                    باور غلط ❌
                  </th>
                  <th className="py-3 px-4 border-b border-amber-200 text-right font-semibold">
                    واقعیت ✔️
                  </th>
                </tr>
              </thead>
              <tbody className="text-stone-700">
                {/* Row 1 */}
                <tr className="hover:bg-amber-50 transition">
                  <td className="py-3 px-4 border-b border-amber-100">
                    قهوه باعث بی‌ خوابی می‌ شود.
                  </td>
                  <td className="py-3 px-4 border-b border-amber-100">
                    تنها اگر دیر مصرف شود. بدن اکثر افراد ۶–۸ ساعت زمان برای دفع کافئین نیاز دارد.
                  </td>
                </tr>

                {/* Row 2 */}
                <tr className="hover:bg-amber-50 transition">
                  <td className="py-3 px-4 border-b border-amber-100">
                    کافئین اعتیادآور است.
                  </td>
                  <td className="py-3 px-4 border-b border-amber-100">
                    از نظر علمی اعتیاد محسوب نمی‌شود؛
                    اما می‌تواند وابستگی خفیف ایجاد کند (مثل سردرد هنگام قطع ناگهانی).
                  </td>
                </tr>

                {/* Row 3 */}
                <tr className="hover:bg-amber-50 transition">
                  <td className="py-3 px-4 border-b border-amber-100">
                    قهوه رشد قد را متوقف می‌کند.
                  </td>
                  <td className="py-3 px-4 border-b border-amber-100">
                    هیچ تحقیق علمی چنین چیزی را ثابت نکرده؛ این باور متعلق به تبلیغات کمپانی‌های رقیب قهوه در قرن ۲۰ است..
                  </td>
                </tr>

                {/* Row 4 */}
                <tr className="hover:bg-amber-50 transition">
                  <td className="py-3 px-4">
                    قهوه فوری (Instant Coffee) کافئین کمتری دارد.
                  </td>
                  <td className="py-3 px-4">
                    بسته به برند متفاوت است؛ اما معمولاً میزان آن مشابه قهوه دم‌شده است.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </Section>
        <Divider />

        <Section
          title="۷. کافئین و انتخاب قهوه مناسب؛ راهنمای خرید برای مشتری ایرانی"
          icon={<Coffee className="w-5 h-5 text-amber-800" />}
          image="/images/guide.jpg"
        >
          <p className="text-sm leading-7 mb-3">
            برای مشتریانی که:
          </p>

          <ul className="list-disc pr-5 space-y-3 text-sm leading-6 text-stone-700">

            <li>
              <span className="font-medium text-amber-900">
                بیداری و انرژی بیشتر می‌خواهند:
              </span>
              <br />
              ترکیب‌های روبوستا + عربیکا یا تک‌روبوستا
            </li>

            <li>
              <span className="font-medium text-amber-900">
                طعم ملایم، عطر بالا و کافئین متوسط می‌خواهند:
              </span>
              <br />
              عربیکا (ترجیحاً تک‌خاستگاه یا اسپشیالتی)
            </li>

            <li>
              <span className="font-medium text-amber-900">
                حساسیت به کافئین دارند:
              </span>
              <br />
              دیکَف یا رُست‌های روشن‌تر + روش دم‌آوری کوتاه
            </li>

          </ul>
        </Section>
        <Divider />

        <Section
          title="۸. جمع‌بندی"
          icon={<Sun className="w-5 h-5 text-yellow-700" />}
          image="/images/caffeine-summary.jpg"
        >
          <p className="text-sm leading-7 text-stone-700">
            کافئین نه دشمن سلامتی است و نه معجزه‌گر. این ماده یکی از شناخته‌شده‌ترین
            و بررسی‌شده‌ترین ترکیبات طبیعی جهان است و مصرف متعادل آن نه‌تنها بی‌خطر
            است، بلکه فواید زیادی نیز دارد.
          </p>

          <p className="text-sm leading-7 text-stone-700 mt-3">
            شناخت علمی کافئین به مصرف‌کنندگان کمک می‌کند انتخاب آگاهانه‌تری داشته
            باشند، و به تولیدکنندگان و رُسترها امکان می‌دهد محصولاتی دقیق‌تر و
            مناسب‌تر برای سلیقه بازار ایران ارائه کنند.
          </p>
        </Section>



        {/* FOOTER */}
        <div className="text-xs text-stone-600 mt-10 flex justify-start">
        ☀️ طراحی: خورشید جاده ابریشم 
        </div>
      </motion.article>
    </div>
  );
}

function Section({ title, icon, children, image }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="mb-10"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-200 shadow">
          {icon}
        </div>
        <h2 className="text-sm font-bold text-amber-900">{title}</h2>
      </div>

      {image && (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
    <div className="overflow-hidden rounded-xl shadow-md border border-amber-100 bg-white">
      <img
        src={image}
        className="w-full h-52 object-cover"
        alt=""
      />
    </div>

    <div className="hidden sm:block overflow-hidden rounded-xl shadow-md border border-amber-100 bg-white">
      <img
        src={image}
        className="w-full h-52 object-cover"
        alt=""
      />
    </div>

    <div className="hidden lg:block overflow-hidden rounded-xl shadow-md border border-amber-100 bg-white">
      <img
        src={image}
        className="w-full h-52 object-cover"
        alt=""
      />
    </div>
  </div>
)}

      <div className="text-sm text-stone-700 leading-7 text-justify">
        {children}
      </div>
    </motion.section>
  );
}

function Divider() {
  return <div className="w-full border-b border-amber-200 my-8" />;
}
